import { Lock, Sparkles } from 'lucide-react';
import { type ReactNode, useEffect, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router';
import { appErrorCode } from '../../../lib/api';
import { trpc } from '../../../lib/trpc';
import { useAccess } from '../../../lib/useAccess';
import { Button } from '../../../ui/Button';
import { SectionHeader } from '../../../ui/PageHeader';
import { Section } from '../../../ui/Section';
import { EmptyState, ErrorState, ListSkeleton } from '../../../ui/States';
import { TabPanel, Tabs } from '../../../ui/Tabs';
import { RememberDialog } from '../../copilot/components/RememberDialog';
import { TOUCH_BUTTON } from '../../mobile';
import { SettingsHeader } from '../pages/SettingsLayout';
import { AiJobsHistory } from './AiJobsHistory';
import { AiModelSection } from './AiModelSection';
import { AiUsageSection } from './AiUsageSection';
import { ProfileCard } from './ProfileCard';
import { ProposalsSection } from './ProposalsSection';
import { SpeechSettingsSection } from './SpeechSettingsSection';

type Tab = 'pamiec' | 'model' | 'historia';
const TABS: readonly { value: Tab; label: string }[] = [
  { value: 'pamiec', label: 'Pamięć' },
  { value: 'model', label: 'Model i limity' },
  { value: 'historia', label: 'Historia' },
];

function isTab(v: string | null): v is Tab {
  return v === 'pamiec' || v === 'model' || v === 'historia';
}

/**
 * Ustawienia → AI i pamięć (czeka na projekt w Figmie; wzorce z Ustawień i Wiedzy), zakładki w adresie
 * (`?zakladka=model|historia`):
 *  - Pamięć: profile OWNER.md i BUSINESS.md z historią wersji i diffem, propozycje zmian pamięci, „Zapamiętaj…”
 *    (propozycja od AI z akceptacją). Widzą właściciel, admin i manager; edytują i decydują właściciel i admin.
 *  - Model i limity: dostawca, modele per profil, budżety, limity, cennik (owner/admin/manager) + zużycie w miesiącu
 *    (pozostali: swoje zużycie).
 *  - Historia: zadania AI (dostawca, symulacja, koszt) — osoba swoje, manager+ wszystkie.
 */
export function AiMemoryPage() {
  const access = useAccess();
  const [params, setParams] = useSearchParams();
  const raw = params.get('zakladka');
  const tab: Tab = isTab(raw) ? raw : 'pamiec';
  const [remember, setRemember] = useState(false);

  const setTab = (next: string) => {
    const p = new URLSearchParams(params);
    if (next === 'pamiec') p.delete('zakladka');
    else p.set('zakladka', next);
    setParams(p, { replace: true });
  };

  return (
    <div>
      <SettingsHeader
        title="AI i pamięć"
        description="Pamięć o Tobie i firmie, model AI i jego koszty. Każda zmiana pamięci to nowa wersja; AI tylko proponuje."
        actions={
          access.isManagerOrAbove && tab === 'pamiec' ? (
            <Button variant="secondary" icon={Sparkles} onClick={() => setRemember(true)} className={TOUCH_BUTTON}>
              Zapamiętaj…
            </Button>
          ) : undefined
        }
      />
      <Tabs value={tab} onValueChange={setTab} items={[...TABS]} aria-label="AI i pamięć" className="mt-6">
        <TabPanel value="pamiec" className="pt-6">
          {access.isManagerOrAbove ? (
            <MemoryContent />
          ) : (
            <Section>
              <EmptyState
                icon={Lock}
                title="Pamięć AI widzi właściciel"
                description="Profile OWNER.md i BUSINESS.md widzą właściciel, administratorzy i managerowie."
              />
            </Section>
          )}
        </TabPanel>
        <TabPanel value="model" className="flex flex-col gap-4 pt-6 md:gap-6">
          {access.isManagerOrAbove ? (
            <>
              <AiModelSection />
              <SpeechSettingsSection />
            </>
          ) : (
            <p className="text-body-s text-fg-secondary">
              Dostawcę, model i limity AI ustawia właściciel, administrator albo manager.
            </p>
          )}
          <AiUsageSection />
        </TabPanel>
        <TabPanel value="historia" className="pt-6">
          <AiJobsHistory />
        </TabPanel>
      </Tabs>
      {access.isManagerOrAbove && <RememberDialog open={remember} onOpenChange={setRemember} />}
    </div>
  );
}

function MemoryContent() {
  const overview = trpc.context.overview.useQuery();
  const location = useLocation();

  // /ustawienia/ai#owner (z listy Wiedzy): przewiń do profilu po wczytaniu
  useEffect(() => {
    if (!overview.data || !location.hash) return;
    document.getElementById(location.hash.slice(1))?.scrollIntoView?.({ block: 'start' });
  }, [overview.data, location.hash]);

  let profiles: ReactNode;
  if (overview.isPending) profiles = <ListSkeleton rows={2} />;
  else if (overview.isError)
    profiles =
      appErrorCode(overview.error) === 'FORBIDDEN' ? (
        <EmptyState icon={Lock} title="Brak dostępu do pamięci AI" />
      ) : (
        <ErrorState onRetry={() => void overview.refetch()} retrying={overview.isFetching} />
      );
  else
    profiles = (
      <div className="flex flex-col gap-6">
        {overview.data.profiles.map((p) => (
          <ProfileCard key={`${p.kind}:${p.versionNo}`} profile={p} />
        ))}
      </div>
    );

  return (
    <div className="flex flex-col gap-4 md:gap-6">
      <Section aria-label="Pamięć">
        <SectionHeader tone="strong" title="Pamięć" />
        <p className="mb-5 text-body-s text-fg-secondary">
          OWNER.md opisuje Twój styl i zasady decyzji, BUSINESS.md ofertę, stawki i procesy. Bez danych prywatnych.
        </p>
        {profiles}
      </Section>
      {overview.data && <ProposalsSection pending={overview.data.pendingProposals} />}
    </div>
  );
}
